/**
 * TEST 서버 전용 미션완료테스트.
 * 회원/밴드 QR → 오늘 active 세트 완료상태 조회 → 체크 목록을 한 번에 저장한다.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrcodeOutlined } from '@ant-design/icons';
import QRScanner from '../components/QRScanner';
import StoreBadge from '../components/StoreBadge';
import useUsbQrReader from '../hooks/useUsbQrReader';
import { extractQuestScanCode } from '../utils/qrUtils';
import { isBridgeApp } from '../utils/isBridgeApp';
import { getQuestTestProgress, saveQuestTestProgress } from '../services/api';
import './QuestCompletionTestPage.css';

const IS_KIOSK = isBridgeApp();

const MISSION_TYPE_LABELS = {
  qr: 'QR',
  ox_quiz: 'OX 퀴즈',
  picture_quiz: '그림 퀴즈',
  funny_photo: '카메라',
  coupon_use: '쿠폰 사용',
  coin_deposit: '주화 저축',
  manual_check: '수동 확인',
};

const errorText = (error, fallback) =>
  error?.message || error?.response?.data?.message || fallback;

export const completedIdsFromProgress = (progress) =>
  new Set((progress?.spots || []).filter((spot) => spot.completed).map((spot) => Number(spot.id)));

export const toggleCompletedId = (selected, spotId) => {
  const next = new Set(selected);
  const id = Number(spotId);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

const sortedIds = (selected) => [...selected].sort((a, b) => a - b);

export default function QuestCompletionTestPage() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [memberQr, setMemberQr] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // {ok,text}

  // QRScanner의 디코드 콜백은 최초 클로저를 오래 유지하므로 상태 가드는 ref가 권위다.
  const loadingRef = useRef(false);
  const progressRef = useRef(null);

  const applyProgress = useCallback((next) => {
    progressRef.current = next;
    setProgress(next);
    setSelected(completedIdsFromProgress(next));
  }, []);

  const handleScan = useCallback(async (rawCode) => {
    // 퀘스트 전용 합성 파서 — 여권(ZLP_)까지 받는다. 공유 extractQRCode 는 넓히지 않는다(S10)
    const code = extractQuestScanCode(rawCode) || rawCode;
    if (!code || loadingRef.current || progressRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setMessage(null);
    try {
      const data = await getQuestTestProgress(code);
      setMemberQr(code);
      applyProgress(data);
    } catch (error) {
      setMessage({ ok: false, text: errorText(error, '미션 상태를 불러오지 못했습니다.') });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [applyProgress]);

  useUsbQrReader({
    enabled: !progress && !loading,
    onScan: handleScan,
    minLength: 4,
  });

  const resetScan = useCallback(() => {
    progressRef.current = null;
    setProgress(null);
    setSelected(new Set());
    setMemberQr('');
    setMessage(null);
  }, []);

  const originalIds = useMemo(() => completedIdsFromProgress(progress), [progress]);
  const isDirty = useMemo(() => {
    if (!progress || originalIds.size !== selected.size) return Boolean(progress);
    return [...originalIds].some((id) => !selected.has(id));
  }, [progress, originalIds, selected]);

  const save = async () => {
    if (!progress?.active_set || saving || !isDirty) return;
    setSaving(true);
    setMessage(null);
    try {
      const data = await saveQuestTestProgress(
        memberQr,
        progress.active_set.id,
        sortedIds(selected),
      );
      applyProgress(data);
      setMessage({
        ok: true,
        text: data.all_completed
          ? '전체 완료로 저장했습니다. 주키퍼 본부에서 완료 연출을 확인할 수 있습니다.'
          : `미션 상태를 저장했습니다. (${data.done}/${data.total})`,
      });
    } catch (error) {
      setMessage({ ok: false, text: errorText(error, '미션 상태 저장에 실패했습니다.') });
    } finally {
      setSaving(false);
    }
  };

  const spots = progress?.spots || [];
  const memberName = progress?.member_name || '친구';

  return (
    <div className="qct-page">
      <header className="qct-header">
        <button type="button" className="qct-back" onClick={() => navigate('/')}>← 메뉴</button>
        <div className="qct-title">
          <div className="qct-title-line">
            <h1>미션완료테스트</h1>
            <span className="qct-test-badge">TEST 전용</span>
          </div>
          <StoreBadge />
        </div>
        <div className="qct-header-spacer" />
      </header>

      <main className="qct-main">
        {!progress ? (
          <section className="qct-scan-panel">
            <div className="qct-scan-copy">
              <h2>회원 QR 스캔</h2>
              <p>오늘의 미션 완료상태를 불러옵니다.</p>
            </div>
            <div className="qct-scanner">
              {IS_KIOSK ? (
                <div className="qct-usb-prompt">
                  <QrcodeOutlined />
                  <strong>QR 리더기로 스캔</strong>
                </div>
              ) : (
                // 🔴 카메라 경로에도 동일 파서 주입 (USB/카메라 비대칭 방지 — S10)
                <QRScanner
                  onScan={handleScan}
                  enabled={!loading}
                  facingMode="environment"
                  extract={extractQuestScanCode}
                />
              )}
            </div>
            {loading && <p className="qct-loading">미션 상태를 불러오는 중…</p>}
            {message && <div className="qct-alert error">{message.text}</div>}
          </section>
        ) : (
          <section className="qct-editor">
            <div className="qct-summary">
              <div>
                <p className="qct-eyebrow">{progress.active_set?.name || '진행 세트 없음'}</p>
                <h2>{memberName}님의 오늘 미션</h2>
              </div>
              <div className={`qct-count ${selected.size === spots.length && spots.length ? 'complete' : ''}`}>
                <strong>{selected.size}</strong><span>/ {spots.length} 완료</span>
              </div>
            </div>

            {!progress.active_set ? (
              <div className="qct-empty">이 지점에 진행 중인 퀘스트 세트가 없습니다.</div>
            ) : spots.length === 0 ? (
              <div className="qct-empty">현재 세트에 활성 미션이 없습니다.</div>
            ) : (
              <>
                <div className="qct-toolbar">
                  <button type="button" onClick={() => setSelected(new Set(spots.map((s) => Number(s.id))))}>
                    전체 완료
                  </button>
                  <button type="button" onClick={() => setSelected(new Set())}>전체 해제</button>
                  <span>미션 행을 터치해 개별 변경</span>
                </div>

                <div className="qct-list">
                  {spots.map((spot, index) => {
                    const checked = selected.has(Number(spot.id));
                    return (
                      <label className={`qct-row ${checked ? 'checked' : ''}`} key={spot.id}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setSelected((prev) => toggleCompletedId(prev, spot.id))}
                        />
                        <span className="qct-order">{index + 1}</span>
                        <span className="qct-row-copy">
                          <strong>{spot.name}</strong>
                          {spot.description && <small>{spot.description}</small>}
                        </span>
                        <span className="qct-type">
                          {MISSION_TYPE_LABELS[spot.mission_type] || spot.mission_type}
                        </span>
                        <span className="qct-state">{checked ? '완료' : '미완료'}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            <div className="qct-notice">
              전체 완료를 처음 저장하면 설정된 세트 보상이 지급될 수 있습니다.
              기존 지급 이력은 보존되어 반복 테스트해도 같은 보상은 다시 지급되지 않습니다.
            </div>
            {message && <div className={`qct-alert ${message.ok ? 'success' : 'error'}`}>{message.text}</div>}

            <div className="qct-actions">
              <button type="button" className="qct-secondary" onClick={resetScan} disabled={saving}>다른 QR</button>
              {progress.all_completed && (
                <button type="button" className="qct-secondary" onClick={() => navigate('/quest-status')}>
                  주키퍼 본부 열기
                </button>
              )}
              <button
                type="button"
                className="qct-save"
                onClick={save}
                disabled={!progress.active_set || !spots.length || !isDirty || saving}
              >
                {saving ? '저장 중…' : '완료 상태 저장'}
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
